import { Alert, Dropdown, Flex, Statistic, Typography, Button } from "antd";
import { Await, Link, useLoaderData, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Suspense, useState } from "react";
import { useMediaQuery } from "@mui/material";
import {
  ExpiringLeasesHomeStatCard,
  RentDueHomeStatCard,
  ShrinkingButton,
  StatCard,
  StatCardSkeleton,
} from "~/components";
import { ROUTES } from "~/utils";
import { HomePageLoaderType } from "./loaders";
import { HomeActionStatCard } from "./components/HomeActionStatCard";
import { HomeAnalyticsCards } from "./components/HomeAnalyticsCards";

const statGridStyle = (smallScreen: boolean) => ({
  marginTop: 10,
  display: "grid",
  gridTemplateColumns: smallScreen
    ? "minmax(0, 1fr)"
    : "repeat(auto-fit, minmax(25em, 1fr))",
  gap: 10,
});

export const HomePage = () => {
  const loaderData = useLoaderData() as HomePageLoaderType;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const smallScreen = useMediaQuery("(max-width: 465px)");
  const verySmallScreen = useMediaQuery("(max-width: 400px)");
  const operations = loaderData.operations;

  const occupancyTabOptions = [
    t("occupancy_rate"),
    t("vacancy_rate"),
    t("renovation_rate"),
    t("off_market_rate"),
  ];
  const occupancyTabOptionsHelp = [
    t("vacancy_rate_description"),
    t("occupancy_rate_description"),
    t("renovation_rate_description"),
  ];
  const [occupancyTab, setOccupancyTab] = useState(0);

  const totalUnits = operations.unitCount;
  const vacancies = operations.vacancyCount;
  const vacancyPercent =
    totalUnits > 0 ? Math.round((vacancies / totalUnits) * 100) : 0;
  const occupancy =
    totalUnits > 0
      ? Math.round((operations.occupiedCount / totalUnits) * 100)
      : 0;
  const inRenovation = operations.inRenovationCount;
  const renovationPercent =
    totalUnits > 0 ? Math.round((inRenovation / totalUnits) * 100) : 0;
  const offMarket = operations.offMarketCount;
  const offMarketPercent =
    totalUnits > 0 ? Math.round((offMarket / totalUnits) * 100) : 0;
  const occupancyValues = [
    occupancy,
    vacancyPercent,
    renovationPercent,
    offMarketPercent,
  ];
  const showVacancyButton =
    vacancies > 0 && (occupancyTab === 0 || occupancyTab === 1);
  const showRenovationButton = inRenovation > 0 && occupancyTab === 2;

  const showSignInPrompt = !loaderData.isAuthenticated;

  return (
    <>
      {showSignInPrompt && (
        <Alert
          message={t("Read_Only_Mode")}
          description={t("Read_Only_Mode_Description_Home")}
          type="info"
          showIcon
          style={{ marginBottom: "1rem" }}
          action={
            <Button
              size="small"
              type="primary"
              onClick={() =>
                navigate(`/explore?returnUrl=${encodeURIComponent("/")}`)
              }
            >
              {t("Sign_up")}
            </Button>
          }
        />
      )}

      <Alert
        showIcon
        type="info"
        description={
          <div>
            <Typography.Paragraph>
              {t("homepage_alert_banner")}
            </Typography.Paragraph>
            <Typography.Paragraph style={{ margin: 0 }}>
              <Link to="/help">{t("Help")}</Link>
            </Typography.Paragraph>
          </div>
        }
        style={{ padding: smallScreen ? "10px" : undefined }}
      />

      <div style={statGridStyle(smallScreen)} data-tour="home-ops-grid">
        <div data-tour="home-occupancy-card">
          <StatCard
            title={t("Occupancy")}
            description={occupancyTabOptionsHelp[occupancyTab]}
            action={
              <Dropdown
                menu={{
                  items: occupancyTabOptions.map((item, index) => ({
                    key: index.toString(),
                    label: <>{item}</>,
                  })),
                  onClick: ({ key }) => setOccupancyTab(Number(key)),
                }}
                placement="bottomLeft"
                arrow
              >
                <ShrinkingButton arrow={true} small={verySmallScreen}>
                  {occupancyTabOptions[occupancyTab]}
                </ShrinkingButton>
              </Dropdown>
            }
          >
            <Flex style={{ flexWrap: "wrap" }}>
              <Statistic
                value={occupancyValues[occupancyTab]}
                precision={2}
                suffix="%"
              />
              {(showVacancyButton || showRenovationButton) && (
                <ShrinkingButton
                  small={verySmallScreen}
                  onClick={() =>
                    navigate(
                      showVacancyButton
                        ? "/unit?status=Vacant"
                        : "/unit?status=Renovation",
                    )
                  }
                  danger
                  type="primary"
                  style={{ marginLeft: "auto" }}
                >
                  {showVacancyButton
                    ? vacancies === 1
                      ? t("single_vacant_unit")
                      : `${vacancies} ${t("Units_Vacant")}`
                    : inRenovation === 1
                      ? t("single_renovating_unit")
                      : `${inRenovation} ${t("Units_in_Renovation")}`}
                </ShrinkingButton>
              )}
            </Flex>
          </StatCard>
        </div>

        <div data-tour="home-rent-due-card">
          <RentDueHomeStatCard
            neverPaidCount={operations.rentNeverPaidCount}
            inProgressCount={operations.rentInProgressCount}
            neverPaidBalanceDue={operations.rentNeverPaidBalanceDue}
            inProgressBalanceDue={operations.rentInProgressBalanceDue}
            month={operations.rentCollectionMonth}
            year={operations.rentCollectionYear}
            verySmallScreen={verySmallScreen}
          />
        </div>

        <ExpiringLeasesHomeStatCard
          expiringLeases={operations.expiringLeases}
          verySmallScreen={verySmallScreen}
          year={operations.rentCollectionYear}
          month={operations.rentCollectionMonth}
        />

        <HomeActionStatCard
          title={t("Service_Requests")}
          description={t("Service_requests_in_progress_meaning")}
          count={operations.inProgressServiceRequests}
          detailsPath={`${ROUTES.serviceRequest}?status=Running`}
          verySmallScreen={verySmallScreen}
          warning={operations.inProgressServiceRequests > 0}
        />

        <div data-tour="home-leases-review-card">
          <HomeActionStatCard
            title={t("Leases_Awaiting_Review")}
            description={t("Leases_Awaiting_Review_home_description")}
            count={operations.leasesAwaitingReviewCount}
            detailsPath={`${ROUTES.leaseTable}?statuses=Created,UnderReview`}
            verySmallScreen={verySmallScreen}
            warning={operations.leasesAwaitingReviewCount > 0}
          />
        </div>

        <HomeActionStatCard
          title={t("Amenity_Reservations_Pending")}
          description={t("Amenity_Reservations_Pending_home_description")}
          count={operations.pendingAmenityReservationsCount}
          detailsPath={ROUTES.amenity}
          verySmallScreen={verySmallScreen}
          warning={operations.pendingAmenityReservationsCount > 0}
        />

        <HomeActionStatCard
          title={t("Transactions_Pending")}
          description={t("Transactions_Pending_home_description")}
          count={operations.pendingTransactionsCount}
          detailsPath={`${ROUTES.payments}?status=Pending`}
          verySmallScreen={verySmallScreen}
          warning={operations.pendingTransactionsCount > 0}
        />

        {operations.hasShortTermStayInventory ? (
          <>
            <HomeActionStatCard
              title={t("Stay_ops_pending")}
              description={t("Stay_ops_pending_description")}
              count={operations.pendingStaysCount}
              detailsPath={`${ROUTES.stays}?filter=pending`}
              verySmallScreen={verySmallScreen}
              warning={operations.pendingStaysCount > 0}
            />
            <HomeActionStatCard
              title={t("Stay_ops_arrivals")}
              description={t("Stay_ops_arrivals_description")}
              count={operations.arrivalsTodayCount}
              detailsPath={`${ROUTES.stays}?filter=arrivals`}
              verySmallScreen={verySmallScreen}
              warning={operations.arrivalsTodayCount > 0}
            />
            <HomeActionStatCard
              title={t("Stay_ops_departures")}
              description={t("Stay_ops_departures_description")}
              count={operations.departuresTodayCount}
              detailsPath={`${ROUTES.stays}?filter=departures`}
              verySmallScreen={verySmallScreen}
              warning={operations.departuresTodayCount > 0}
            />
            <HomeActionStatCard
              title={t("Stay_ops_noshow")}
              description={t("Stay_ops_noshow_description")}
              count={operations.noShowCount}
              detailsPath={`${ROUTES.stays}?filter=noshow`}
              verySmallScreen={verySmallScreen}
              warning={operations.noShowCount > 0}
            />
          </>
        ) : null}
      </div>

      <Suspense
        fallback={
          <div style={statGridStyle(smallScreen)}>
            {Array.from({ length: 3 }, (_, i) => (
              <StatCardSkeleton key={i} />
            ))}
          </div>
        }
      >
        <Await resolve={loaderData.analytics}>
          {(analytics) => (
            <div style={statGridStyle(smallScreen)}>
              <HomeAnalyticsCards
                analytics={analytics}
                verySmallScreen={verySmallScreen}
              />
            </div>
          )}
        </Await>
      </Suspense>
    </>
  );
};
